import { CartUpdateEvent, ThemeEvents } from '@theme/events';

const SOURCE = 'cavento-cart-enhancements';
const TIMER_KEY = 'cce_cart_reservation_expires_at';
const PROTECTION_OPT_OUT_KEY = 'cce_shipping_protection_opt_out';

let timerInterval = null;
let observer = null;
let mutationInFlight = false;

const rootPath = () => window.Shopify?.routes?.root || '/';
const topRoots = () => [...document.querySelectorAll('[data-cce-top]')];
const protectionRows = () => [...document.querySelectorAll('[data-cce-protection]')];

function money(cents) {
  const currency = window.Shopify?.currency?.active || document.querySelector('[data-cce-top]')?.dataset.marketCurrency || 'USD';
  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency,
    }).format(Number(cents || 0) / 100);
  } catch (_) {
    return `$${(Number(cents || 0) / 100).toFixed(2)}`;
  }
}

async function getCart() {
  const response = await fetch(`${rootPath()}cart.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error('Unable to retrieve cart.');
  return response.json();
}

function sectionIds() {
  return [...new Set(
    [...document.querySelectorAll('cart-items-component[data-section-id]')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean)
  )];
}

function mutationPayload(extra = {}) {
  const ids = sectionIds();
  return {
    ...extra,
    ...(ids.length ? { sections: ids.join(','), sections_url: window.location.pathname } : {}),
  };
}

async function broadcastCart(cart, sections = {}) {
  const event = new CartUpdateEvent(cart, SOURCE, {
    source: SOURCE,
    itemCount: cart.item_count,
    sections,
  });
  document.dispatchEvent(event);
}

async function updateCart(updates) {
  const response = await fetch(`${rootPath()}cart/update.js`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(mutationPayload({ updates })),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.errors) throw new Error(data.description || data.message || 'Cart update failed.');
  await broadcastCart(data, data.sections || {});
  return data;
}

async function changeLine(id, quantity) {
  const response = await fetch(`${rootPath()}cart/change.js`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(mutationPayload({ id, quantity })),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.errors) throw new Error(data.description || data.message || 'Cart update failed.');
  await broadcastCart(data, data.sections || {});
  return data;
}

async function addVariant(variantId, quantity = 1) {
  const response = await fetch(`${rootPath()}cart/add.js`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(mutationPayload({ items: [{ id: Number(variantId), quantity }] })),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.status) throw new Error(data.description || data.message || 'Unable to add item.');
  const cart = await getCart();
  await broadcastCart(cart, data.sections || {});
  return cart;
}

function protectionConfig() {
  const row = document.querySelector('[data-cce-protection]');
  const top = document.querySelector('[data-cce-top]');
  return {
    productId: Number(row?.dataset.productId || top?.dataset.protectionProductId || 0),
    variantId: Number(row?.dataset.variantId || top?.dataset.protectionVariantId || 0),
    preselected: (row?.dataset.preselected || top?.dataset.protectionPreselected || 'true') !== 'false',
  };
}

function getProtectionItem(cart) {
  const { productId } = protectionConfig();
  if (!productId) return null;
  return cart.items.find((item) => Number(item.product_id) === productId) || null;
}

function qualifyingTotal(cart) {
  const protection = getProtectionItem(cart);
  return Number(cart.total_price || 0) - Number(protection?.final_line_price || 0);
}

function merchandiseItemCount(cart) {
  const protection = getProtectionItem(cart);
  return Number(cart.item_count || 0) - Number(protection?.quantity || 0);
}

function updateTop(cart) {
  const total = qualifyingTotal(cart);
  topRoots().forEach((root) => {
    const threshold = Number(root.dataset.shippingThreshold || 0);
    if (!threshold) return;
    const percent = Math.min(100, Math.max(0, (total / threshold) * 100));
    const message = root.querySelector('[data-cce-shipping-message]');
    const fill = root.querySelector('[data-cce-progress-fill]');
    const truck = root.querySelector('[data-cce-progress-truck]');
    const progress = root.querySelector('.cce-progress');

    if (message) {
      const success = root.dataset.successText || 'Congrats! You get FREE shipping!';
      const progressText = root.dataset.progressText || "You're [amount] away from FREE shipping! Add another item and unlock it!";
      message.textContent = total >= threshold ? success : progressText.replace('[amount]', money(threshold - total));
    }
    if (fill) fill.style.width = `${percent}%`;
    if (truck) truck.style.left = `${percent}%`;
    if (progress) progress.setAttribute('aria-valuenow', String(Math.round(percent)));
  });
}

function syncProtection(cart) {
  const item = getProtectionItem(cart);
  protectionRows().forEach((row) => {
    row.dataset.lineKey = item?.key || '';
    const input = row.querySelector('[data-cce-protection-toggle]');
    if (input && !input.disabled) input.checked = Boolean(item);
  });
}

function startTimer(cart, reset = false) {
  const itemCount = merchandiseItemCount(cart);
  const roots = topRoots();
  const banners = roots.map((root) => root.querySelector('[data-cce-reservation]')).filter(Boolean);

  if (!itemCount) {
    sessionStorage.removeItem(TIMER_KEY);
    sessionStorage.removeItem(PROTECTION_OPT_OUT_KEY);
    clearInterval(timerInterval);
    timerInterval = null;
    banners.forEach((banner) => { banner.hidden = true; });
    return;
  }

  banners.forEach((banner) => { banner.hidden = false; });
  const duration = Math.max(60, Number(roots[0]?.dataset.timerDuration || 300)) * 1000;
  let expiresAt = Number(sessionStorage.getItem(TIMER_KEY));

  if (reset || !expiresAt || expiresAt <= Date.now()) {
    expiresAt = Date.now() + duration;
    sessionStorage.setItem(TIMER_KEY, String(expiresAt));
  }

  const tick = () => {
    let remaining = expiresAt - Date.now();
    if (remaining <= 0) {
      expiresAt = Date.now() + duration;
      sessionStorage.setItem(TIMER_KEY, String(expiresAt));
      remaining = duration;
    }
    const totalSeconds = Math.max(0, Math.ceil(remaining / 1000));
    const label = `${String(Math.floor(totalSeconds / 60)).padStart(2, '0')}:${String(totalSeconds % 60).padStart(2, '0')}`;
    document.querySelectorAll('[data-cce-countdown]').forEach((node) => { node.textContent = label; });
  };

  clearInterval(timerInterval);
  tick();
  timerInterval = window.setInterval(tick, 1000);
}

async function ensureProtection(cart) {
  const config = protectionConfig();
  if (!config.preselected || !config.variantId || getProtectionItem(cart)) return cart;
  if (sessionStorage.getItem(PROTECTION_OPT_OUT_KEY) === '1') return cart;
  if (merchandiseItemCount(cart) <= 0 || mutationInFlight) return cart;

  mutationInFlight = true;
  try {
    return await updateCart({ [String(config.variantId)]: 1 });
  } finally {
    mutationInFlight = false;
  }
}

async function removeOrphanProtection(cart) {
  const item = getProtectionItem(cart);
  if (!item || merchandiseItemCount(cart) > 0 || mutationInFlight) return cart;
  mutationInFlight = true;
  try {
    return await changeLine(item.key, 0);
  } finally {
    mutationInFlight = false;
  }
}

async function handleProtection(input) {
  const row = input.closest('[data-cce-protection]');
  const config = protectionConfig();
  const intended = input.checked;
  input.disabled = true;
  row.dataset.error = 'false';

  try {
    const cart = await getCart();
    const item = getProtectionItem(cart);
    let updatedCart = cart;

    if (intended) {
      sessionStorage.removeItem(PROTECTION_OPT_OUT_KEY);
      if (!item && config.variantId) updatedCart = await updateCart({ [String(config.variantId)]: 1 });
    } else {
      sessionStorage.setItem(PROTECTION_OPT_OUT_KEY, '1');
      if (item) updatedCart = await changeLine(item.key, 0);
    }

    syncProtection(updatedCart);
    updateTop(updatedCart);
  } catch (error) {
    console.error('[Cavento cart enhancements]', error);
    input.checked = !intended;
    row.dataset.error = 'true';
  } finally {
    input.disabled = false;
  }
}

async function handleUpsell(button) {
  const row = button.closest('[data-cce-upsell]');
  const variantInput = row?.querySelector('[data-cce-upsell-variant]');
  const variantId = Number(variantInput?.value || 0);
  if (!variantId) return;

  button.disabled = true;
  const text = button.querySelector('.button-text');
  const original = text?.textContent || 'Add';
  if (text) text.textContent = 'Adding…';

  try {
    await addVariant(variantId, 1);
  } catch (error) {
    console.error('[Cavento cart enhancements]', error);
    button.disabled = false;
    if (text) text.textContent = original;
  }
}

function bindControls() {
  document.querySelectorAll('[data-cce-protection-toggle]:not([data-cce-bound])').forEach((input) => {
    input.dataset.cceBound = 'true';
    input.addEventListener('change', () => handleProtection(input));
  });

  document.querySelectorAll('[data-cce-upsell-add]:not([data-cce-bound])').forEach((button) => {
    button.dataset.cceBound = 'true';
    button.addEventListener('click', () => handleUpsell(button));
  });
}

async function refresh({ resetTimer = false, allowProtectionAutoAdd = true } = {}) {
  bindControls();
  try {
    let cart = await getCart();
    cart = await removeOrphanProtection(cart);
    if (allowProtectionAutoAdd) cart = await ensureProtection(cart);
    updateTop(cart);
    syncProtection(cart);
    startTimer(cart, resetTimer);
    return cart;
  } catch (error) {
    console.error('[Cavento cart enhancements]', error);
    return null;
  }
}

function init() {
  bindControls();
  refresh();

  document.addEventListener(ThemeEvents.cartUpdate, (event) => {
    bindControls();
    const cart = event.detail?.resource;
    if (cart?.items) {
      updateTop(cart);
      syncProtection(cart);
      startTimer(cart, event.detail?.sourceId !== SOURCE);
      queueMicrotask(() => refresh({ allowProtectionAutoAdd: event.detail?.sourceId !== SOURCE }));
    } else {
      queueMicrotask(() => refresh({ resetTimer: true }));
    }
  });

  if (!observer && document.body) {
    observer = new MutationObserver(() => bindControls());
    observer.observe(document.body, { childList: true, subtree: true });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
